import type { Order, ShippingCost } from "types";
import type { FeePayload, ShipPayload } from "components/SignageCreateOrder/types";
import type { SelectedProduct } from "components/SignageEditOrder/types";
import {
  readResolveTaxable,
  resolveCatalogProductLineTaxable,
  resolveShippingTaxableForPayload,
  resolveSpecialFeeTaxableForPayload,
  resolveSpecialFeeRequiredForPayload,
} from "orderResolve/utils/readResolveTaxable";

export type BuildSignageEditOrderUpdatePayloadDeps = {
  nextTaxStatus: string;
  order: Order | null;
  taxBreakdown: { product: number; shipping: number; specialFee: number };
  selectedProducts: SelectedProduct[];
  ProductObjs: any[] | null | undefined;
  customproducts: any[] | null | undefined;
  perRowDiscountMapToShow: Record<string, number>;
  shipPayload: ShipPayload;
  feePayload: FeePayload;
  shippingCosts: ShippingCost[];
  /** Vendor special-fee catalog — source of truth for taxable flags on submit. */
  specialFeeCatalog?: unknown[] | null;
  productTaxMap: Record<string, number>;
  getUnitPriceToUse: (row: any) => number;
  /** resolve-assignment-discount effective_price only (no manual override, no coupon). */
  getEffectiveUnitPrice: (row: any) => number;
  /** Manual override → user-edited price; otherwise base/actual price (base_price from assignment API). */
  getUnitRetailPriceForPayload: (row: any) => number;
  getCustomProductKey: (cp: { product_id: string; variant_id: string }, idx: number) => string;
  selectedBillingAddress: any;
  selectedShippingAddress: any;
  selectedOfficeBillingAddress: any;
  selectedOfficeShippingAddress: any;
  selectedOfficeAgent: any;
  selectedAgent: any;
  selectedOffice: any;
  offices: any[] | null | undefined;
  selectedBrokerageFirm: any;
  userAppliedCoupon: boolean;
  couponDiscounts: any[] | null | undefined;
  selectedShippingMethod: { value: string; label: string; cost: number } | null;
  linkedProjectId: string | null;
  linkedProjectNumber: string | null;
  isSignageTeammember: boolean;
  parentSignageId: string | null;
  parentSignageName: string | null;
  userId: string | null | undefined;
  userName: string | null | undefined;
};

export type SignageEditOrderBuyerContext = {
  agent_name: string | null;
  finalBillingAddress: any;
  finalShippingAddress: any;
};

export function buildSignageEditOrderUpdatePayload(
  values: any,
  deps: BuildSignageEditOrderUpdatePayloadDeps
): { updatePayload: Record<string, unknown>; buyer: SignageEditOrderBuyerContext } {
  const {
    nextTaxStatus,
    order,
    taxBreakdown,
    selectedProducts,
    ProductObjs,
    customproducts,
    perRowDiscountMapToShow,
    shipPayload,
    feePayload,
    shippingCosts,
    specialFeeCatalog,
    productTaxMap,
    getUnitPriceToUse,
    getUnitRetailPriceForPayload,
    getCustomProductKey,
    selectedBillingAddress,
    selectedShippingAddress,
    selectedOfficeBillingAddress,
    selectedOfficeShippingAddress,
    selectedOfficeAgent,
    selectedAgent,
    selectedOffice,
    offices,
    selectedBrokerageFirm,
    userAppliedCoupon,
    couponDiscounts,
    selectedShippingMethod,
    linkedProjectId,
    linkedProjectNumber,
    isSignageTeammember,
    parentSignageId,
    parentSignageName,
    userId,
    userName,
  } = deps;

  const asNum = (v: any) => Number(v || 0);
  const round2 = (n: number) => Math.round(asNum(n) * 100) / 100;
  const keyForRow = (r: { productId: string; variantId: string }) => `${r.productId}:${r.variantId}`;

  const getLineItemIdFromOrderItem = (item: unknown): string => {
    if (!item || typeof item !== "object") return "";
    const raw = (item as { _id?: unknown })._id;
    if (raw == null) return "";
    if (typeof raw === "object") {
      const oid = (raw as { $oid?: string }).$oid;
      if (oid) return String(oid).trim();
      const str = (raw as { toString?: () => string }).toString?.();
      return str && str !== "[object Object]" ? String(str).trim() : "";
    }
    return String(raw).trim();
  };

  const orderItemsFromGet = Array.isArray((order as { items?: unknown[] } | null)?.items)
    ? ((order as { items: unknown[] }).items as Record<string, unknown>[])
    : [];

  const mapUiShipToApi = (r: any) => {
    const id = String(r.shipping__id ?? r.shipping_id ?? "");
    const cost = shippingCosts.find((c) => String(c._id) === id) as
      | { is_customer_pickup?: boolean; shipping_type?: string }
      | undefined;
    const fromRowPickup = r?.is_customer_pickup === true;
    const fromCostPickup = cost?.is_customer_pickup === true;
    const rowType = String(r.shipping_type ?? "");
    const costType = String(cost?.shipping_type ?? "");
    return {
      shipping_id: id,
      shipping_name: String(r.shipping_name ?? ""),
      shipping_type: rowType || costType || (id === "custom-manual" ? "MANUAL" : ""),
      shipping_amount: asNum(r.shipping_amount),
      shipping_tax_percentage: asNum(r.shipping_tax_percentage),
      shipping_tax_amount: asNum(r.shipping_tax_amount),
      shipping_taxable: resolveShippingTaxableForPayload(r as Record<string, unknown>, shippingCosts),
      shipping_delivery_date: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
      shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
      is_customer_pickup: fromRowPickup || fromCostPickup,
    };
  };

  function buildItemTaxInfoAtEdit(
    row: { productId: string; variantId: string; sku: string; unitPrice: number; quantity: number },
    pTaxMap: Record<string, number>,
    shippingAddress: any,
    isTaxable?: boolean
  ) {
    const k = keyForRow(row);
    const lineTax = asNum(pTaxMap[k] || 0);
    const qty = Math.max(1, asNum(row.quantity || 1));
    const unitTax = qty > 0 ? lineTax / qty : 0;
    const base = asNum(row.unitPrice || 0) * qty;
    const ratePct = base > 0 ? (lineTax / base) * 100 : 0;
    const appliedState = String(
      shippingAddress?.address_details?.stateCode ||
        shippingAddress?.address_details?.state ||
        ""
    );
    return {
      tax_percentage: round2(ratePct),
      unit_product_tax_amount: round2(unitTax),
      total_product_tax_amount: round2(lineTax),
      tax_details: {
        tax_rate: round2(ratePct),
        tax_amount: round2(lineTax),
        applied_state: appliedState,
      },
      taxable: typeof isTaxable === "boolean" ? isTaxable : true,
    };
  }

  let brokerageFirmId: string | null = null;
  let officeInfo: any = null;
  let agentId: string | null = null;
  let agent_name: string | null = null;
  let customerId: string | null = null;
  let inventorySource: "agent" | "office" = "agent";
  let finalBillingAddress: any = null;
  let finalShippingAddress: any = null;

  finalBillingAddress = selectedBillingAddress;

  finalShippingAddress =
    values.isSameAsBilling && selectedBillingAddress ? finalBillingAddress : selectedShippingAddress;

  if (selectedOfficeAgent) {
    agentId = selectedOfficeAgent?.value;
    const first = selectedOfficeAgent?.firstName ?? "";
    const last = selectedOfficeAgent?.lastName ?? "";
    agent_name = (selectedAgent?.label ?? `${first} ${last}`).trim();
    customerId = selectedOfficeAgent?.value;
    inventorySource = "agent";
    brokerageFirmId = selectedBrokerageFirm?.value || null;
    officeInfo = null;
    finalBillingAddress = selectedBillingAddress;
    finalShippingAddress = values.isSameAsBilling && selectedBillingAddress
      ? finalBillingAddress
      : selectedShippingAddress;
  } else if (selectedAgent) {
    agentId = selectedAgent?.value;
    const first = selectedAgent?.firstName ?? "";
    const last = selectedAgent?.lastName ?? "";
    agent_name = (selectedAgent?.label ?? `${first} ${last}`).trim();
    customerId = selectedAgent?.value;
    inventorySource = "agent";
    officeInfo = null;
    finalBillingAddress = selectedBillingAddress;
    finalShippingAddress = values.isSameAsBilling && selectedBillingAddress
      ? finalBillingAddress
      : selectedShippingAddress;
  } else if (selectedOffice) {
    const selectedOfficeData = offices?.find((o) => o._id === selectedOffice?.value);
    const primaryAddress = selectedOfficeData?.officeAddress?.[0];

    officeInfo = {
      _id: selectedOffice?.value,
      officeName: selectedOffice?.label,
      officeType: selectedOffice?.label,
      officeAddress: primaryAddress,
      status: true,
    };

    customerId = selectedBrokerageFirm?.value || null;
    brokerageFirmId = selectedBrokerageFirm?.value || null;
    inventorySource = "office";
    agentId = null;
    finalBillingAddress = selectedOfficeBillingAddress;
    finalShippingAddress =
      values.isSameAsBilling && selectedOfficeBillingAddress
        ? finalBillingAddress
        : selectedOfficeShippingAddress;
  }

  const shippingRowsFromPayload = Array.isArray(shipPayload?.shipping_option) ? shipPayload.shipping_option : [];
  const shippingRowsFromOrder = (order?.order_charges?.[0]?.shipping_option ?? []) as any[];
  const shippingRows = shippingRowsFromPayload.length > 0 ? shippingRowsFromPayload : shippingRowsFromOrder;
  const feeRowsFromPayload = Array.isArray(feePayload?.specialfee) ? feePayload.specialfee : [];
  const feeRowsFromOrder = (order?.order_charges?.[0]?.specialfee ?? []) as any[];
  const specialRows = feeRowsFromPayload.length > 0 ? feeRowsFromPayload : feeRowsFromOrder;

  const shippingAddrForTax =
    selectedShippingAddress ||
    selectedOfficeShippingAddress ||
    selectedBillingAddress ||
    selectedOfficeBillingAddress ||
    null;

  const perRowAlloc = perRowDiscountMapToShow || {};

  const products = (selectedProducts || []).map((product) => {
    const productObj = ProductObjs?.find((p) => p.product_id === product.productId);
    const variant = productObj?.product.variants.find((v: any) => v.variant_id === product.variantId);
    const inventory = variant?.inventory || [];

    let storageLocation: any = null;
    if (inventory.length === 1) {
      const w: any = inventory[0];
      storageLocation = {
        warehouse_id: w.warehouse_id,
        warehouse: w.warehouse_name,
        zone_id: w.zone_id,
        zone: w.zone_name,
        rack_id: w.rack_id,
        rack: w.rack_name,
        bin_id: w.bin_id,
        bin: w.bin_name,
      };
    } else if (inventory.length > 1) {
      const w: any = inventory.find((x: any) => Number(x.available) > 0) || inventory[inventory.length - 1];
      storageLocation = {
        warehouse_id: w.warehouse_id,
        warehouse: w.warehouse_name,
        zone_id: w.zone_id,
        zone: w.zone_name,
        rack_id: w.rack_id,
        rack: w.rack_name,
        bin_id: w.bin_id,
        bin: w.bin_name,
      };
    }

    const key = keyForRow({ productId: product.productId, variantId: product.variantId });
    const qty = Math.max(1, asNum(product.quantity));
    const purchase_price = asNum(productObj?.product?.purchase_price || 0);
    const baseUnit = getUnitPriceToUse(product);
    const rowDiscTotal = asNum(perRowAlloc[key] || 0);

    const lineBase = baseUnit * qty;
    const lineAfter = Math.max(0, lineBase - rowDiscTotal);
    const changedUnit = round2(lineAfter / qty);
    const perUnitDisc = round2(rowDiscTotal / qty);

    const isTaxable = resolveCatalogProductLineTaxable({
      catalogProduct: productObj?.product as Record<string, unknown> | undefined,
      row: product as Record<string, unknown>,
      savedTaxInfo: (product as { item_tax_info?: Record<string, unknown> }).item_tax_info,
    });

    const taxInfo = buildItemTaxInfoAtEdit(
      {
        productId: product.productId,
        variantId: product.variantId,
        sku: product.sku,
        unitPrice: changedUnit,
        quantity: qty,
      },
      productTaxMap,
      shippingAddrForTax,
      isTaxable
    );

    const tax_amount = asNum(taxInfo?.total_product_tax_amount || 0);

    const item_sub_total = round2(lineAfter);
    const total_price = round2(item_sub_total + tax_amount);

    const productTypeOnly = String(
      (product as any).product_type ?? productObj?.product?.product_type ?? "simple"
    ).trim() || "simple";

    const productLineItemId = String((product as { line_item_id?: string }).line_item_id ?? "").trim();
    const originalFromOrder =
      (productLineItemId
        ? orderItemsFromGet.find(
            (it) =>
              getLineItemIdFromOrderItem(it) === productLineItemId &&
              String(it.inventory_source ?? "") !== "custom"
          )
        : undefined) ||
      orderItemsFromGet.find(
        (it) =>
          String(it.product_id ?? "") === String(product.productId) &&
          String(it.variant_id ?? "") === String(product.variantId) &&
          String(it.inventory_source ?? "") !== "custom"
      );
    const existingLineItemId =
      productLineItemId || getLineItemIdFromOrderItem(originalFromOrder);

    return {
      // Send _id only for products already saved on the order; newly added omit it.
      ...(existingLineItemId ? { _id: existingLineItemId } : {}),
      product_id: product.productId,
      variant_id: product.variantId,
      warehouse_id: storageLocation?.warehouse_id || "",
      product_name: variant?.variant_name || "",
      sku: variant?.sku || "",
      images: product?.images || [],
      quantity: qty,

      unit_purchase_price: purchase_price,
      unit_retail_price: round2(getUnitRetailPriceForPayload(product)),
      changed_unit_price: round2(changedUnit),
      discount_on_unit_quantity: perUnitDisc,
      discount_on_total_quantity: round2(rowDiscTotal),
      inventory_source: "signage",
      item_sub_total,
      total_price,

      attributes: product.attributesList?.[0]?.attributes || {},
      storage_location: storageLocation,
      is_production: product.is_production,
      customizations: {
        template_name: product.customizations?.template_name || "",
        fields: product.customizations?.fields || [],
      },
      item_tax_info: taxInfo,
      is_taxable: isTaxable,
      ...(product.design_id || product.studio_design_id
        ? {
            design_id: String(product.design_id || product.studio_design_id || ""),
            design_name: String(product.design_name || product.studio_design_name || ""),
            preview_url: String(product.preview_url || product.studio_preview_id || ""),
          }
        : {}),
      product_type: productTypeOnly,
    };
  });

  const shippingAddrForTaxState =
    selectedShippingAddress || selectedOfficeShippingAddress || selectedBillingAddress || selectedOfficeBillingAddress;
  const custom_products = (customproducts || []).map((p, idx) => {
    const customKey = getCustomProductKey(p, idx);
    const preservedCustomTaxable =
      typeof p?.item_tax_info?.taxable === "boolean"
        ? p.item_tax_info.taxable
        : typeof p?.is_taxable === "boolean"
          ? p.is_taxable
          : undefined;
    const lineTax = preservedCustomTaxable === false ? 0 : asNum(productTaxMap[customKey] ?? 0);
    const qty = Math.max(1, asNum(p.quantity || 1));
    const unitPrice = asNum(p.Price || 0);
    const item_sub_total = round2(unitPrice * qty);
    const total_price = round2(item_sub_total + lineTax);
    const ratePct = unitPrice * qty > 0 ? (lineTax / (unitPrice * qty)) * 100 : 0;
    const addr = shippingAddrForTaxState as any;
    const appliedState = String(addr?.address_details?.stateCode || addr?.address_details?.state || "");
    const customLineItemId = String((p as { line_item_id?: string }).line_item_id ?? "").trim();
    const originalCustomFromOrder =
      (customLineItemId
        ? orderItemsFromGet.find(
            (it) =>
              getLineItemIdFromOrderItem(it) === customLineItemId &&
              String(it.inventory_source ?? "") === "custom"
          )
        : undefined) ||
      orderItemsFromGet.find(
        (it) =>
          String(it.product_id ?? "") === String(p.product_id) &&
          String(it.variant_id ?? "") === String(p.variant_id) &&
          String(it.inventory_source ?? "") === "custom"
      );
    const existingCustomLineItemId =
      customLineItemId || getLineItemIdFromOrderItem(originalCustomFromOrder);

    return {
      ...(existingCustomLineItemId ? { _id: existingCustomLineItemId } : {}),
      product_id: p.product_id,
      variant_id: p.variant_id,
      unit_purchase_price: unitPrice,
      unit_retail_price: unitPrice,
      changed_unit_price: unitPrice,
      discount_on_unit_quantity: 0.0,
      discount_on_total_quantity: 0.0,
      total_price,
      item_sub_total,
      storage_location: null,
      images: [],
      customizations: {},
      product_name: p.item_name || "",
      is_production: true,
      quantity: p.quantity,
      sku: `custom_sku_${p.product_id}`,
      unit_price: unitPrice,
      inventory_source: "custom",
      ...(typeof preservedCustomTaxable === "boolean" ? { is_taxable: preservedCustomTaxable } : {}),
      item_tax_info: {
        tax_percentage: round2(ratePct),
        unit_product_tax_amount: round2(qty > 0 ? lineTax / qty : 0),
        total_product_tax_amount: round2(lineTax),
        tax_details: { tax_rate: round2(ratePct), tax_amount: round2(lineTax), applied_state: appliedState },
        ...(typeof preservedCustomTaxable === "boolean" ? { taxable: preservedCustomTaxable } : {}),
      },
      custom_information: p.custom_information,
      custom_attributes: {
        custom_information: p.custom_information,
      },
      attributes: {
        length: p.length?.toString() || "",
        width: p.width?.toString() || "",
        height: p.height?.toString() || "",
        weight: p.weight?.toString() || "",
      },
    };
  });

  const shipSum = shippingRows.reduce((t, r) => t + Number(r?.shipping_amount || 0), 0);
  const feeSum = specialRows.reduce((t, r) => t + Number(r?.specialfee_amount || 0), 0);

  const productTaxAmount = Number(taxBreakdown.product || 0);
  const customProductsTaxSum = round2(
    (customproducts || []).reduce((s, p, idx) => s + asNum(productTaxMap[getCustomProductKey(p, idx)] ?? 0), 0)
  );
  const shippingTaxAmount = Number(taxBreakdown.shipping || 0);
  const specialFeeTaxAmount = Number(taxBreakdown.specialFee || 0);
  const totalTax = round2(productTaxAmount + customProductsTaxSum + shippingTaxAmount + specialFeeTaxAmount);

  const productsSubTotal = round2(
    (products || []).reduce((s, it: any) => s + asNum(it.item_sub_total || it.total_price || 0), 0)
  );
  const customProductsSubtotal = round2(
    (customproducts || []).reduce((s, cp) => s + asNum(cp.Price || 0) * Math.max(0, asNum(cp.quantity || 1)), 0)
  );

  const discount_amount = round2(Object.values(perRowAlloc).reduce((s, n) => s + asNum(n), 0));

  const promo_code = userAppliedCoupon
    ? couponDiscounts?.[0]?.id || ""
    : order?.discount_code || "";

  const subtotal_amount = round2(productsSubTotal + customProductsSubtotal);
  const subtotal_amounts = round2(productsSubTotal + customProductsSubtotal);
  const total_amount = round2(subtotal_amount + shipSum + feeSum - discount_amount + totalTax);

  const overAllAmountwithouttax = round2(subtotal_amount + shipSum + feeSum);
  const overallPct = overAllAmountwithouttax > 0 ? (totalTax / overAllAmountwithouttax) * 100 : 0;

  const updatePayload: Record<string, unknown> = {
    buyer_type: values.userType,
    signage_id: isSignageTeammember && parentSignageId ? parentSignageId : userId,
    signage_company_name: isSignageTeammember && parentSignageName ? parentSignageName : userName,

    office: officeInfo,
    brokerage_firm_name: selectedBrokerageFirm?.label,
    brokerage_firm_id: brokerageFirmId,
    customer_id: customerId,
    team_member_id: values.teamMemberId,
    team_member_name: values.teamMemberName,
    agent_id: agentId,
    agent_name: agent_name,

    customer_first_name: values.userFirstName,
    customer_last_name: values.userLastName,
    customer_email: values.userEmail,
    customer_phone: values.userPhone,

    shipping_address: finalShippingAddress,
    billing_address: finalBillingAddress,
    both_address_same: values.isSameAsBilling,

    items: [...products, ...custom_products],
    inventory_source: inventorySource,

    // manual_shipping_method: {
    //   _id: selectedShippingMethod?.value,
    //   name: selectedShippingMethod?.label,
    //   cost: selectedShippingMethod?.cost,
    // },

    discount_amount,
    discount_code: promo_code,
    promo_code,

    tax_amount: totalTax,
    shipping_amount: shipSum,
    subtotal_amount,
    total_amount,

    notes: values.notes,

    order_tax_summary: {
      tax_inclusive: false,
      over_all_tax_amount: totalTax,
      over_all_tax_percentage: Number(overallPct.toFixed(4)),
      over_all_taxable_base: overAllAmountwithouttax,
      tax_status: nextTaxStatus,
    },

    order_charges: [
      {
        shipping_option: shippingRows.map(mapUiShipToApi),
        specialfee: (specialRows || []).map((f: any) => ({
          specialfee_id: String(f.specialfee_id ?? ""),
          specialfee_name: String(f.specialfee_name ?? ""),
          specialfee_amount: asNum(f.specialfee_amount),
          specialfee_tax_percentage: asNum(f.specialfee_tax_percentage),
          specialfee_tax_amount: asNum(f.specialfee_tax_amount),
          specialfee_taxable: resolveSpecialFeeTaxableForPayload(
            f as Record<string, unknown>,
            specialFeeCatalog
          ),
          required: resolveSpecialFeeRequiredForPayload(
            f as Record<string, unknown>,
            specialFeeCatalog
          ),
        })),
      },
    ],
    ...(linkedProjectId
      ? {
          project_id: linkedProjectId,
          ...(linkedProjectNumber ? { project_number: linkedProjectNumber } : {}),
        }
      : {}),
  };

  return {
    updatePayload,
    buyer: {
      agent_name,
      finalBillingAddress,
      finalShippingAddress,
    },
  };
}
